import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate, useLocation } from 'react-router'
import type {
  FindPaginatedLineItemsResults,
  ModelsPublicReturnLineItem,
  ProcessesApiFindLineItemsRequest
} from '@itsrever/dashboard-api'
import { useDebounce } from '@/hooks/use-debounce'
import { useItems } from '@/hooks/use-items'
import { useEcommerceFilter } from '@/hooks/use-selected-ecommerce-ids'
import { ReturnStatus } from '@/utils'
import {
  ALL_ITEMS,
  lineItemsRequestFrom
} from '@/domain/processes/list-request'
import { ListTabs, useListTabsPage } from '@/ui/shared/list-tabs'
import { columns } from './items-columns'

const ACTION_REQUIRED = [ReturnStatus.ReviewRequired].toString()
const PENDING_TO_RECEIVE = [
  ReturnStatus.Started,
  ReturnStatus.Collected
].toString()
const RECEIVED = ReturnStatus.Completed

const DEFAULT_PAGE_LIMIT = 15

export const ItemsTabs = () => {
  const { t } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { offsetFor } = useListTabsPage(ALL_ITEMS, DEFAULT_PAGE_LIMIT)

  const ecommerceId = useEcommerceFilter()

  const debouncedSearch = useDebounce(
    searchParams.get('search') ?? undefined,
    300
  )

  const filterParams: ProcessesApiFindLineItemsRequest = {
    ...lineItemsRequestFrom(searchParams, {
      ecommerceId,
      freetext: debouncedSearch
    }),
    limit: DEFAULT_PAGE_LIMIT
  }

  const all = useItems({
    ...filterParams,
    returnStatus: undefined,
    offset: offsetFor(ALL_ITEMS)
  })

  const pendingToReceive = useItems({
    ...filterParams,
    returnStatus: PENDING_TO_RECEIVE,
    offset: offsetFor(PENDING_TO_RECEIVE)
  })

  const actionRequired = useItems({
    ...filterParams,
    returnStatus: ACTION_REQUIRED,
    offset: offsetFor(ACTION_REQUIRED)
  })

  const received = useItems({
    ...filterParams,
    returnStatus: RECEIVED,
    offset: offsetFor(RECEIVED)
  })

  const onRowClick = (data: ModelsPublicReturnLineItem) => {
    searchParams.set('ecommerce_id', data.return_process?.ecommerce_id ?? '')
    setSearchParams(searchParams)

    navigate(
      {
        pathname: `/returns/orders/${data.return_process?.process_id}`,
        search: searchParams.toString()
      },
      {
        state: { previousPathname: location.pathname }
      }
    )
  }

  return (
    <ListTabs
      tabs={[
        {
          ...tabFrom(all),
          value: ALL_ITEMS,
          label: t('topbar_items.all')
        },
        {
          ...tabFrom(pendingToReceive),
          value: PENDING_TO_RECEIVE,
          label: t('topbar_items.pending')
        },
        {
          ...tabFrom(actionRequired),
          value: ACTION_REQUIRED,
          label: t('topbar_items.review_required'),
          hidden: !actionRequired.data?.rowcount
        },
        {
          ...tabFrom(received),
          value: RECEIVED,
          label: t('topbar_items.received')
        }
      ]}
      allTab={ALL_ITEMS}
      pageSize={DEFAULT_PAGE_LIMIT}
      columns={columns}
      onRowClick={onRowClick}
    />
  )
}

const tabFrom = ({
  data,
  isPending,
  isFetching
}: {
  data?: FindPaginatedLineItemsResults
  isPending: boolean
  isFetching: boolean
}) => ({
  rows: data && (data.line_items ?? []),
  count: data?.rowcount ?? 0,
  isPending,
  isFetching
})
